import { cn, Input } from '@ds';
import { Controller, FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { CSSchedulerSettingsDeductionRulesFormType } from '@lp/features/Scheduler/SchedulerClinicalSpecialistsSettings/types';
import { getNumberInputValue, getNumberInputValueHandler } from '@lp/features/Scheduler/utils';
import { CS_PERIOD_LENGTH } from '@lp/features/Scheduler/SchedulerClinicalSpecialists/period.utils';
import { getDeductionRuleTotal } from '@lp/features/Scheduler/SchedulerClinicalSpecialistsSettings/config';

type CSDeductionRuleTableRowProps = {
  absenceDays: number;
  isReadOnly: boolean;
};

export const CSDeductionRuleTableRow = ({ absenceDays, isReadOnly }: CSDeductionRuleTableRowProps) => {
  const { control } = useFormContext<CSSchedulerSettingsDeductionRulesFormType>();
  const rowName = `byAbsenceDays.${absenceDays}` as const;
  const rule = useWatch({ control, name: rowName });

  const daysPresent = CS_PERIOD_LENGTH - absenceDays;
  const atHub = rule?.atHub ?? 0;
  const officeDay = rule?.officeDay ?? 0;
  const noCall = rule?.noCall ?? 0;
  const weekendOff = rule?.weekendOff ?? 0;
  const flexDays = atHub + officeDay;
  const offDays = noCall + weekendOff;
  const total = rule ? getDeductionRuleTotal(rule, absenceDays) : absenceDays;
  const isOverTotal = total > CS_PERIOD_LENGTH;
  const isMismatch = total !== CS_PERIOD_LENGTH;

  const renderInput = (name: FieldPath<CSSchedulerSettingsDeductionRulesFormType>, max: number) => (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
        <Input
          type="number"
          min={0}
          max={max}
          aria-label={name}
          isInvalid={invalid}
          isDisabled={isReadOnly}
          errorMessage={error?.message}
          value={getNumberInputValue(value as number)}
          onValueChange={getNumberInputValueHandler(onChange)}
          {...field}
        />
      )}
    />
  );

  return (
    <tr className="even:bg-base-90">
      <td className="px-3 border-e border-base-60 text-right text-m-r">{absenceDays}</td>
      <td className="px-2 py-1">{renderInput(`${rowName}.onCall`, 28)}</td>
      <td className="px-2 py-1 border-e border-base-60">{renderInput(`${rowName}.weekendOnCall`, 8)}</td>
      <td className="px-2 py-1">{renderInput(`${rowName}.atHub`, 28)}</td>
      <td className="px-2 py-1">{renderInput(`${rowName}.officeDay`, 28)}</td>
      <td className="px-3 border-e border-base-60 text-right text-m-r">{flexDays}</td>
      <td className="px-2 py-1">{renderInput(`${rowName}.noCall`, 28)}</td>
      <td className="px-2 py-1">{renderInput(`${rowName}.weekendOff`, 8)}</td>
      <td className="px-3 border-e border-base-60 text-right text-m-r">{offDays}</td>
      <td className="px-3 text-right text-m-r">{daysPresent}</td>
      <td
        className={cn('px-3 text-right text-m-r', isMismatch && (isOverTotal ? 'text-danger-20' : 'text-warning-20'))}
      >
        {isMismatch ? (
          <>
            {total} / <span className="text-base-5">{CS_PERIOD_LENGTH}</span>
          </>
        ) : (
          total
        )}
      </td>
    </tr>
  );
};
